import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { ArrowLeft, Clock } from "lucide-react";
import SEOHead from "../components/SEOHead";
import { SubjectFeedbackSection } from "../components/FeedbackSections";
import { API_BASE_URL, BACKEND_URL } from "../lib/site";

const API = API_BASE_URL;
const BACKEND = BACKEND_URL;

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAiSearchCover =
    article?.slug === "when-the-first-touchpoint-is-an-answer-instead-of-a-link";
  const isMissingLayerCover =
    article?.slug === "missing-layer-b2c-conversion-funnels";
  const isLabDiamondCover =
    article?.slug === "lab-grown-diamond-luxury-brand";

  useEffect(() => {
    let ignore = false;

    setLoading(true);
    axios
      .get(`${API}/articles/${slug}`)
      .then((res) => {
        if (ignore) return;
        setArticle(res.data);
        axios.post(`${API}/articles/${slug}/view`).catch(() => {});
      })
      .catch(() => {
        if (!ignore) setArticle(null);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-16 flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-[#4B5563]">Article not found.</p>
        <Link to="/writing" className="text-[#002FA7] font-medium flex items-center gap-1">
          <ArrowLeft size={14} /> Back to Writing
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-16 page-enter">
      <SEOHead
        title={article.title}
        description={article.excerpt || `${article.title} - by Niharika Patel`}
        image={
          article.cover_image
            ? article.cover_image.startsWith("/api")
              ? `${BACKEND}${article.cover_image}`
              : article.cover_image
            : undefined
        }
        url={`${BACKEND}/writing/${article.slug}`}
        type="article"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.excerpt,
          author: { "@type": "Person", name: "Niharika Patel", url: BACKEND },
          publisher: { "@type": "Person", name: "Niharika Patel" },
          datePublished: article.created_at,
          url: `${BACKEND}/writing/${article.slug}`,
          ...(article.cover_image && {
            image: article.cover_image.startsWith("/api")
              ? `${BACKEND}${article.cover_image}`
              : article.cover_image
          })
        }}
      />

      <div className="border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4">
          <Link
            to="/writing"
            className="text-sm text-[#4B5563] flex items-center gap-1 hover:text-[#002FA7] transition-colors"
          >
            <ArrowLeft size={14} /> Writing
          </Link>
        </div>
      </div>

      {article.cover_image && (
        <div
          className={`w-full overflow-hidden ${
            isMissingLayerCover
              ? "aspect-[3/1] bg-[#F7F0D7]"
              : isLabDiamondCover
                ? "aspect-video sm:aspect-[21/7] bg-[#050608]"
                : "aspect-[4/3] sm:aspect-[21/7]"
          }`}
        >
          <img
            src={article.cover_image}
            alt={article.title}
            className={`w-full h-full ${
              isMissingLayerCover
                ? "object-contain"
                : isLabDiamondCover
                  ? "object-contain sm:object-cover object-center"
                : `object-cover ${isAiSearchCover ? "object-left sm:object-center" : "object-center"}`
            }`}
            style={isLabDiamondCover ? { objectPosition: "68% center" } : undefined}
          />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 lg:px-0 py-10 sm:py-12" data-testid="article-content">
        <div className="flex gap-2 mb-4 flex-wrap">
          {(article.tags || []).map((tag) => (
            <span
              key={tag}
              className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#002FA7] bg-blue-50 px-2 py-0.5"
            >
              {tag}
            </span>
          ))}
        </div>

        <h1 className="font-['Outfit'] text-[1.65rem] sm:text-5xl font-semibold text-[#0A0A0A] leading-tight mb-4">
          {article.title}
        </h1>

        {article.excerpt && (
          <p className="max-w-2xl text-lg sm:text-[1.35rem] text-[#4B5563] leading-relaxed italic mb-6">
            {article.excerpt}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#9CA3AF] mb-8 pb-8 border-b border-[#E5E7EB]">
          <span className="flex items-center gap-1.5">
            <Clock size={13} /> {article.read_time} min read
          </span>
          {article.views > 0 && <span>{article.views} views</span>}
          <span>By Niharika Patel</span>
        </div>

        <div className="prose-article">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {article.content}
          </ReactMarkdown>
        </div>
      </article>

      <SubjectFeedbackSection
        subjectType="article"
        subjectId={article.id}
        placeholder="What felt useful, sharp, incomplete, or worth debating?"
      />
    </div>
  );
}
